import React, { useEffect, useState, useRef } from 'react';
import { SVGIcon } from '../SVGIcons';
import { countryPhoneMasks, countryPhoneRegex } from './helpers/constants';

interface IInput {
  hasSearchIcon?: boolean;
  className?: string;
  style?: string;
  value?: string | number | readonly string[];
  onChange: (key: any, value: any) => void;
}

const InputPhone = ({
  hasSearchIcon = false,
  className = '',
  style,
  onChange,
  value,
}: IInput) => {
  const [countryCode, setCountryCode] = useState('+1');
  const [cellNumber, setCellNumber] = useState('');

  const isValidPhone = (phone: string, locale: string): boolean => {
    const phoneRegex = countryPhoneRegex[locale];
    return phoneRegex ? phoneRegex.test(phone) : false;
  };

  const warning = cellNumber !== '' && !isValidPhone(cellNumber, countryCode);

  const handleCountryCode = (value: string) => {
    setCountryCode(value);
    setCellNumber('');
  };

  useEffect(() => {
    if (value === '') {
      setCountryCode('+1');
      setCellNumber('');
    }
  }, [value]);

  return (
    <div
      className={`mr-4 flex h-12 rounded-md border-2 ${
        warning
          ? 'border-erro-bordocaulequente50 ring-erro-bordocaulequente50'
          : 'border-neutra3-cinzaquente70'
      } bg-white ${style}`}
    >
      <select
        id="countryCode"
        className="border-none bg-transparent ring-0 focus:ring-0"
        value={countryCode}
        onChange={(e: any) => handleCountryCode(e.target.value)}
      >
        {Object.keys(countryPhoneMasks).map(code => (
          <option key={code} value={code}>
            {code}
          </option>
        ))}
      </select>
      {hasSearchIcon && (
        <SVGIcon
          name="search"
          color="#45433A"
          size="small"
          strokeColor="black"
          strokeWidth={0.5}
          className="absolute left-2 top-[5px]"
        />
      )}
      <InputMasked
        countryCode={countryCode}
        cellNumber={cellNumber}
        setCellNumber={setCellNumber}
        isValidPhone={isValidPhone}
        onChange={onChange}
        warning={warning}
        className={className}
      />
    </div>
  );
};

const InputMasked = ({
  countryCode,
  cellNumber,
  warning,
  setCellNumber,
  isValidPhone,
  onChange,
  className,
}: {
  countryCode: string;
  cellNumber: string;
  setCellNumber: (value: string) => void;
  isValidPhone: (phone: string, locale: string) => boolean;
  onChange: (key: any, value: any) => void;
  warning: boolean;
  className: string;
}): React.JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, mask: string) => {
    const digits = e.target.value.replace(/\D/g, '');
    const input = e.target;

    let formattedNumber = mask;
    let digitIndex = 0;

    for (let i = 0; i < formattedNumber.length; i++) {
      if (formattedNumber[i] === '9') {
        if (digitIndex < digits.length) {
          formattedNumber = formattedNumber.substring(0, i) + digits[digitIndex] + formattedNumber.substring(i + 1);
          digitIndex++;
        } else {
          formattedNumber = formattedNumber.substring(0, i) + '_' + formattedNumber.substring(i + 1);
        }
      }
    }

    const value = formattedNumber;
    const oldDigits = cellNumber.replace(/\D/g, '');
    const newDigits = formattedNumber.replace(/\D/g, '');

    let selectionStart = input.selectionStart ?? 0;

    if (oldDigits.length < newDigits.length) {
      selectionStart++;
    } else if (oldDigits.length > newDigits.length) {
      selectionStart--;
    }

    setCellNumber(value);
    onChange('cellNumber', countryCode + value)

    setTimeout(() => {
      if (inputRef.current) {
        let cursorPosition = value.indexOf('_');
        if (cursorPosition === -1) {
          cursorPosition = value.length;
        }
        inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
      }
    }, 0);

    
  };
  return (
    <input
      ref={inputRef}
      type="text"
      className={`w-full border-none bg-transparent py-1 pl-2 pr-4 text-sm placeholder:text-neutra3-cinzaquente50 ${!warning ? 'focus-visible:rounded-md focus-visible:ring-2 focus-visible:ring-neutra3-cinzaquente50' : 'focus-visible:ring-erro-bordocaulequente50'} ${className}`}
      value={cellNumber}
      onChange={(e: any) => handleChange(e, countryPhoneMasks[countryCode])}
    />
  );
};

export default InputPhone;
